import type { DateRange } from "react-day-picker";
import type { TWRChart, TWRPoint, BenchmarkPoint } from "@/schemas";

/**
 * Rebase a full TWR chart so the first visible point starts at 0%.
 */
export function rebaseTWRChart(twrChart: TWRChart): TWRChart {
  if (!twrChart.twr_points?.length) {
    return twrChart;
  }

  const baseTwr = twrChart.twr_points[0].twr_cumulative;
  const baseFactor = 1 + baseTwr;

  // Safety guard: cannot rebase if factor is non-positive.
  if (baseFactor <= 0) {
    return twrChart;
  }

  const rebasedPoints: TWRPoint[] = twrChart.twr_points.map((point) => ({
    ...point,
    twr_cumulative: (1 + point.twr_cumulative) / baseFactor - 1,
  }));

  return {
    ...twrChart,
    twr_points: rebasedPoints,
    last_timestamp: rebasedPoints[rebasedPoints.length - 1].timestamp,
  };
}

/**
 * Slice and rebase TWR data for a custom date range.
 * 
 * Algorithm:
 * 1. Snap selected dates to available TWR point timestamps
 * 2. Slice the points within the range
 * 3. Rebase cumulative TWR to start at 0% at the selected start date
 */
export function sliceAndRebaseTWR(
  twrChart: TWRChart,
  range: DateRange
): TWRChart | null {
  if (!twrChart.twr_points?.length || !range.from) {
    return twrChart;
  }

  const points = twrChart.twr_points;
  
  // Convert timestamps to numbers for comparison
  const timestamps = points.map(p => new Date(p.timestamp).getTime());
  
  // Normalize selected dates
  const fromMs = new Date(range.from).setHours(0, 0, 0, 0);
  const toMs = range.to 
    ? new Date(range.to).setHours(23, 59, 59, 999)
    : new Date().setHours(23, 59, 59, 999); // Open-ended to now if no end date

  // Snap startIdx: find last index where timestamp <= fromMs (fallback to 0)
  let startIdx = 0;
  for (let i = timestamps.length - 1; i >= 0; i--) {
    if (timestamps[i] <= fromMs) {
      startIdx = i;
      break;
    }
  }

  // Snap endIdx: find last index where timestamp <= toMs (fallback to last)
  let endIdx = timestamps.length - 1;
  for (let i = timestamps.length - 1; i >= 0; i--) {
    if (timestamps[i] <= toMs) {
      endIdx = i;
      break;
    }
  }

  // Ensure valid range
  if (endIdx < startIdx) {
    endIdx = startIdx;
  }

  // Slice points
  const slicedPoints = points.slice(startIdx, endIdx + 1);

  if (slicedPoints.length < 1) {
    return null;
  }

  const slicedChart: TWRChart = {
    ...twrChart,
    twr_points: slicedPoints,
    last_timestamp: slicedPoints[slicedPoints.length - 1].timestamp,
  };

  return rebaseTWRChart(slicedChart);
}

/**
 * Merge TWR and benchmark data for the chart.
 */
export function mergeTWRWithBenchmark(
  twrChart: TWRChart | null,
  benchmarkData: BenchmarkPoint[] | null,
  benchmarkEnabled: boolean
): { timestamp: string; twr: number; benchmark?: number }[] {
  if (!twrChart || !twrChart.twr_points?.length) {
    return [];
  }

  const twrPoints = twrChart.twr_points.map((p) => ({
    timestamp: p.timestamp,
    twr: p.twr_cumulative * 100,
    benchmark: undefined as number | undefined,
  }));

  // If benchmark is not enabled or no data, return just TWR
  if (!benchmarkEnabled || !benchmarkData || benchmarkData.length === 0) {
    return twrPoints;
  }

  // Benchmark points are already aligned to TWR timestamps in alignBenchmarkToTWR.
  const benchmarkByTimestamp = new Map(
    benchmarkData.map((point) => [point.timestamp, point.return_pct * 100] as const)
  );

  return twrPoints.map((point) => ({
    ...point,
    benchmark: benchmarkByTimestamp.get(point.timestamp),
  }));
}

/**
 * Calculate Y-axis domain for TWR chart including benchmark data.
 */
export function calculateTWRYAxisDomain(
  twrChart: TWRChart | null,
  benchmarkData: BenchmarkPoint[] | null,
  benchmarkEnabled: boolean
): [number, number] {
  const allValues: number[] = [];
  
  // Add TWR values
  if (twrChart && twrChart.twr_points.length > 0) {
    allValues.push(...twrChart.twr_points.map((point) => point.twr_cumulative * 100));
  }
  
  // Add benchmark values if enabled
  if (benchmarkEnabled && benchmarkData && benchmarkData.length > 0) {
    allValues.push(...benchmarkData.map((point) => point.return_pct * 100));
  }
  
  if (allValues.length === 0) {
    return [-5, 5];
  }
  
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const padding = (max - min) * 0.1 || 1;
  return [min - padding, max + padding];
}
